import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from '../utils/apiClient';
import Select from "react-select";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Swal from 'sweetalert2';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { Calendar, MapPin, Clock, Trophy, DollarSign, Users, Sun, Moon, CheckCircle, AlertCircle } from 'lucide-react';

/**
 * AddEventPage - Manage Events
 *
 * Per GCHL Brand Guide:
 * - Forest green primary with gold accents
 * - Navy dark mode with proper contrast
 * - Card-based layout with proper shadows
 */
const AddEventPage = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [date, setDate] = useState("");
  const [teeTime, setTeeTime] = useState("");
  const [isMajor, setIsMajor] = useState(false);
  const [cost, setCost] = useState(0);
  const [fedupEligible, setFedupEligible] = useState(true);
  const [numTeetimes, setNumTeetimes] = useState(5);
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const navigate = useNavigate();

  // Initialize dark mode from localStorage/system preference
  useEffect(() => {
    const stored = localStorage.getItem('theme');
    if (stored) {
      setIsDarkMode(stored === 'dark');
    } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setIsDarkMode(true);
    }
  }, []);

  // Sync dark mode to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  useEffect(() => {
    const fetchCoursesAndEvents = async () => {
      try {
        const coursesResponse = await apiClient.get('/admin/courses');
        const formattedCourses = coursesResponse.data.map((course) => ({
          value: course.id,
          label: course.name,
        }));
        setCourses(formattedCourses);

        const eventsResponse = await apiClient.get('/admin/events?type=upcoming');
        const eventsData = eventsResponse.data?.events || eventsResponse.data || [];
        const formattedEvents = eventsData.map((event) => ({
          value: event.id,
          label: `${new Date(event.date).toLocaleDateString()} - ${event.course_name}`,
        }));
        setEvents([
          { value: "add-new", label: "-- Add New Event --", isSpecial: true },
          ...formattedEvents,
        ]);
      } catch (error) {
        // Silently handle fetch error
      }
    };

    fetchCoursesAndEvents();
  }, []);

  const formatDateForInput = (isoDate) => {
    const date = new Date(isoDate);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatTimeForInput = (dbTime) => {
    if (!dbTime) return "";
    const [hours, minutes] = dbTime.split(":");
    return `${hours}:${minutes}`;
  };

  const handleEventChange = (selectedOption) => {
    if (selectedOption.value === "add-new") {
      setSelectedEvent(null);
      setSelectedCourse(null);
      setDate("");
      setTeeTime("");
      setIsMajor(false);
    } else {
      const selected = events.find((event) => event.value === selectedOption.value);
      setSelectedEvent(selected);
      fetchEventDetails(selected.value);
    }
  };

  const fetchEventDetails = async (eventId) => {
    try {
      const response = await apiClient.get(`/admin/events/${eventId}`);
      const event = response.data.details;

      setSelectedCourse({
        value: event.course_id,
        label: event.course_name,
      });
      setDate(formatDateForInput(event.date));
      setTeeTime(formatTimeForInput(event.tee_time));
      setIsMajor(event.is_major);
      setCost(event.cost);
      setFedupEligible(event.fedup_eligible);
      setNumTeetimes(event.num_teetimes);
    } catch (error) {
      // Silently handle event details fetch error
    }
  };

  const handleCourseChange = (selectedOption) => {
    setSelectedCourse(selectedOption);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCourse || !date) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Fields',
        text: 'Please select a course and date.',
      });
      return;
    }

    try {
      if (selectedEvent) {
        await apiClient.put(
          `/admin/events/${selectedEvent.value}`,
          {
            courseId: selectedCourse.value,
            date,
            teeTime,
            isMajor,
            cost,
            fedupEligible,
            numTeetimes,
          }
        );
        Swal.fire({
          icon: 'success',
          title: 'Event Updated!',
          text: 'The event has been successfully updated.',
          confirmButtonText: 'Great!',
        }).then(() => {
          navigate('/events');
        });

      } else {
        await apiClient.post(
          '/admin/events/events',
          {
            courseId: selectedCourse.value,
            date,
            teeTime,
            isMajor,
            cost,
            fedupEligible,
            numTeetimes,
          }
        );
        Swal.fire({
          icon: 'success',
          title: 'Event Added!',
          text: 'The new event has been successfully added.',
          confirmButtonText: 'Great!',
        }).then(() => {
          navigate('/events');
        });
      }

    } catch (error) {
      setFeedbackMessage({
        type: "error",
        text: `Failed to save event. Please try again.`,
      });
    }
  };

  // Custom styles for react-select with GCHL branding
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#1e293b' : 'white',
      borderColor: state.isFocused
        ? (isDarkMode ? '#fbbf24' : '#166534')
        : (isDarkMode ? '#334155' : '#d1d5db'),
      borderRadius: '0.75rem',
      padding: '0.25rem',
      boxShadow: state.isFocused
        ? (isDarkMode ? '0 0 0 2px rgba(251, 191, 36, 0.3)' : '0 0 0 2px rgba(22, 101, 52, 0.3)')
        : 'none',
      '&:hover': {
        borderColor: isDarkMode ? '#fbbf24' : '#166534',
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#1e293b' : 'white',
      borderRadius: '0.75rem',
      border: isDarkMode ? '1px solid #334155' : '1px solid #e5e7eb',
      overflow: 'hidden',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.data.isSpecial
        ? state.isFocused
          ? (isDarkMode ? '#78350f' : '#fde68a')
          : (isDarkMode ? '#451a03' : '#fef3c7')
        : state.isFocused
          ? (isDarkMode ? '#334155' : '#f3f4f6')
          : 'transparent',
      color: state.data.isSpecial
        ? (isDarkMode ? '#fbbf24' : '#b45309')
        : (isDarkMode ? '#f1f5f9' : '#1f2937'),
      fontWeight: state.data.isSpecial ? "bold" : "normal",
      cursor: "pointer",
      padding: '0.75rem 1rem',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: isDarkMode ? '#f1f5f9' : '#1f2937',
    }),
    input: (provided) => ({
      ...provided,
      color: isDarkMode ? '#f1f5f9' : '#1f2937',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: isDarkMode ? '#64748b' : '#9ca3af',
    }),
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-navy-950 flex flex-col transition-colors duration-300">
      <Navbar />

      {/* Header Section */}
      <div className="bg-white dark:bg-navy-900 border-b border-gray-200 dark:border-navy-800">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-display font-bold text-forest-900 dark:text-white tracking-tight">
                Manage Events
              </h1>
              <p className="text-gray-600 dark:text-navy-400 mt-1">
                {selectedEvent ? "Edit existing event" : "Create a new event"}
              </p>
            </div>

            {/* Dark Mode Toggle */}
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="
                flex items-center gap-2 px-4 py-2 rounded-full
                bg-gray-100 dark:bg-navy-800
                text-gray-700 dark:text-gray-200
                hover:bg-gray-200 dark:hover:bg-navy-700
                transition-colors duration-200
                border border-gray-200 dark:border-navy-700
              "
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? (
                <>
                  <Sun className="w-5 h-5 text-gold-400" />
                  <span className="text-sm font-medium hidden sm:inline">Light</span>
                </>
              ) : (
                <>
                  <Moon className="w-5 h-5 text-navy-600" />
                  <span className="text-sm font-medium hidden sm:inline">Dark</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-grow max-w-4xl mx-auto px-4 py-8 w-full">
        {/* Feedback Message */}
        {feedbackMessage && (
          <div
            className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
              feedbackMessage.type === "success"
                ? "bg-seafoam-light/10 text-seafoam-dark dark:bg-seafoam-dark/20 dark:text-seafoam-light border border-seafoam-light/30"
                : "bg-sunset-light/10 text-sunset-dark dark:bg-sunset-dark/20 dark:text-sunset-light border border-sunset-light/30"
            }`}
          >
            {feedbackMessage.type === "success" ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            {feedbackMessage.text}
          </div>
        )}

        <Card>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Event Selector */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Select Event to Edit or Add New
              </label>
              <Select
                options={events}
                value={selectedEvent}
                onChange={handleEventChange}
                placeholder="-- Add New Event --"
                styles={customStyles}
              />
            </div>

            {/* Course Selector */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-forest-600 dark:text-forest-400" />
                  Course
                </div>
              </label>
              <Select
                options={courses}
                value={selectedCourse}
                onChange={handleCourseChange}
                placeholder="Search or select a course"
                styles={customStyles}
              />
            </div>

            {/* Date and Time Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-forest-600 dark:text-forest-400" />
                    Date
                  </div>
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="
                    w-full px-4 py-3
                    bg-white dark:bg-navy-800
                    border border-gray-300 dark:border-navy-600
                    rounded-xl
                    text-gray-900 dark:text-gray-100
                    focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
                    focus:border-forest-500 dark:focus:border-gold-400
                    transition-colors duration-200
                  "
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-forest-600 dark:text-forest-400" />
                    Tee Time
                  </div>
                </label>
                <input
                  type="time"
                  value={teeTime}
                  onChange={(e) => setTeeTime(e.target.value)}
                  className="
                    w-full px-4 py-3
                    bg-white dark:bg-navy-800
                    border border-gray-300 dark:border-navy-600
                    rounded-xl
                    text-gray-900 dark:text-gray-100
                    focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
                    focus:border-forest-500 dark:focus:border-gold-400
                    transition-colors duration-200
                  "
                  required
                />
              </div>
            </div>

            {/* Event Type Checkboxes */}
            <div className="bg-gray-50 dark:bg-navy-800/50 rounded-xl p-4 space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-gold-500" />
                Event Type
              </h3>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isMajor}
                  onChange={(e) => setIsMajor(e.target.checked)}
                  className="
                    w-5 h-5 rounded border-gray-300 dark:border-navy-600
                    text-gold-500 focus:ring-gold-400
                    dark:bg-navy-700
                  "
                />
                <div>
                  <span className="font-medium text-gray-900 dark:text-gray-100">Major Event</span>
                  <p className="text-sm text-gray-500 dark:text-gray-400">1.25x points multiplier</p>
                </div>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={fedupEligible}
                  onChange={(e) => setFedupEligible(e.target.checked)}
                  className="
                    w-5 h-5 rounded border-gray-300 dark:border-navy-600
                    text-forest-600 focus:ring-forest-500
                    dark:bg-navy-700
                  "
                />
                <div>
                  <span className="font-medium text-gray-900 dark:text-gray-100">FedUp Eligible</span>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Regular Sunday event counting toward season</p>
                </div>
              </label>
            </div>

            {/* Cost and Tee Times Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-forest-600 dark:text-forest-400" />
                    Cost per Player
                  </div>
                </label>
                <input
                  type="number"
                  value={cost}
                  onChange={(e) => setCost(parseFloat(e.target.value))}
                  className="
                    w-full px-4 py-3
                    bg-white dark:bg-navy-800
                    border border-gray-300 dark:border-navy-600
                    rounded-xl
                    text-gray-900 dark:text-gray-100
                    focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
                    focus:border-forest-500 dark:focus:border-gold-400
                    transition-colors duration-200
                  "
                  step="0.01"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-forest-600 dark:text-forest-400" />
                    Number of Tee Times
                  </div>
                </label>
                <input
                  type="number"
                  value={numTeetimes}
                  onChange={(e) => setNumTeetimes(parseInt(e.target.value))}
                  className="
                    w-full px-4 py-3
                    bg-white dark:bg-navy-800
                    border border-gray-300 dark:border-navy-600
                    rounded-xl
                    text-gray-900 dark:text-gray-100
                    focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
                    focus:border-forest-500 dark:focus:border-gold-400
                    transition-colors duration-200
                  "
                  min="1"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button type="submit" variant="action" className="w-full md:w-auto">
                {selectedEvent ? "Update Event" : "Add Event"}
              </Button>
            </div>
          </form>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default AddEventPage;
